import { PayloadAction } from '@reduxjs/toolkit';

import { PatientAllergiesClient, PatientAllergyDto, PatientAllergySaveRequest } from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

// A save either succeeds (list refetched) or comes back with a readable message for the modal to show inline.
// A stale rowVersion and a vendor-owned row arrive as succeeded:false envelopes, but the save gate's own rules
// are thrown: the server raises MedOneValidationException, which the API maps to a 400 ProblemDetails, so the
// catch has to read them out. (SCRUM-1117 rationale, transferred to SCRUM-1372.)
export type SavePatientAllergyResult = { succeeded: boolean; message?: string };

// SCRUM-1372 - the stale-patient guard reads census.currentCensus, which only the chart maintains. Census-less
// routes (the Medical Summary) can still have it pinned to a patient edited earlier in the shift, so their
// allergies would be discarded as another patient's; they opt out. The ownership tag on the list is what keeps
// that safe: the list renders rows only while they are tagged to the patient it is showing.
type PatientAllergyGuardOptions = { bypassActivePatientGuard?: boolean };

const shouldDiscard = (state: RootState, patientId: number, options?: PatientAllergyGuardOptions) =>
    !options?.bypassActivePatientGuard && shouldSkipByPatientId(state.census, patientId);

export const reducers = {
    setPatientAllergiesLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.patientAllergiesLoading = action.payload;
    },

    // The list and the patient it belongs to are written together so they can never disagree: a caller cannot
    // store rows and forget to say whose they are.
    setPatientAllergies: (state: CensusState, action: PayloadAction<{ patientId: number; allergies: PatientAllergyDto[] }>) => {
        state.patientAllergies = action.payload.allergies;
        state.patientAllergiesPatientId = action.payload.patientId;
    },
};

export function fetchPatientAllergies(patientId: number, options?: PatientAllergyGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(censusSlice.actions.setPatientAllergiesLoading(true));
        // SCRUM-1372 - both mount points read this one list, so ANOTHER patient's rows have to go before the new
        // fetch resolves; the loading mask is translucent and they would stay readable underneath it. Rows that
        // already belong to this patient stay put: every save and delete awaits a refetch, and blanking those
        // flashes "No coded allergies recorded." on the chart, the same misread on the common path.
        if (getState().census.patientAllergiesPatientId !== patientId) {
            dispatch(censusSlice.actions.setPatientAllergies({ patientId, allergies: [] }));
        }

        const client = new PatientAllergiesClient(null, Axios);

        try {
            const response = await client.getByPatient(patientId);

            // Guard against race condition: discard if user navigated to a different patient.
            if (shouldDiscard(getState(), patientId, options)) {
                return;
            }

            // A host that opts out of the census guard has nothing else ordering two in-flight fetches, so an
            // earlier patient's response could land last and re-tag the shared list, leaving the patient on
            // screen reading "No coded allergies recorded.". The tag this fetch set is what makes it current.
            if (getState().census.patientAllergiesPatientId !== patientId) {
                return;
            }

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setPatientAllergies({ patientId, allergies: response.result.entity ?? [] }));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        } finally {
            // In a finally, not after the try: the race guard returns early, and a discarded fetch that never
            // cleared the flag would leave the table spinning for good.
            dispatch(censusSlice.actions.setPatientAllergiesLoading(false));
        }
    };
}

// allergyId null = create, otherwise update. Refetches on success so the server-resolved state lands.
export function savePatientAllergy(
    patientId: number,
    allergyId: number | null,
    request: PatientAllergySaveRequest,
    options?: PatientAllergyGuardOptions
): AppThunk<Promise<SavePatientAllergyResult>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientAllergiesClient(null, Axios);

        try {
            const response = allergyId != null ? await client.update(patientId, allergyId, request) : await client.create(patientId, request);

            if (response.result.succeeded) {
                // Skip the refetch if the user has since navigated to another patient.
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientAllergies(patientId, options));
                }

                return { succeeded: true };
            }

            return { succeeded: false, message: response.result.message };
        } catch (error) {
            // Only a 400 carries the save gate's own wording; a 500's detail is server internals, not a rule a
            // clinician can act on, so it stays behind the generic message.
            const validationDetail = error?.status === 400 ? error?.response?.detail : null;

            // The modal surfaces this inline; don't also toast via handleError, or a thrown save (e.g. a dropped
            // connection) would report the same failure twice.
            return { succeeded: false, message: validationDetail || 'Something went wrong saving the allergy.' };
        }
    };
}

// Removal has no inline surface (the row's Popconfirm closes either way), so a failure toasts.
export function removePatientAllergy(patientId: number, allergyId: number, options?: PatientAllergyGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientAllergiesClient(null, Axios);

        try {
            const response = await client.deleteAllergy(patientId, allergyId);

            if (response.result.succeeded) {
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientAllergies(patientId, options));
                }
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

// One shared instance, never an inline []: a fresh array on every render would break the selector's referential
// check and re-render the list on every unrelated census change.
const NO_ALLERGIES: PatientAllergyDto[] = [];

// SCRUM-1372 - the list and its ownership tag are read together, so there is no ungated way to render the rows.
// The fetch that replaces them is dispatched from an effect, which React commits AFTER the render, so reading
// the list alone paints the previous patient's allergies under the new patient's header.
export const selectPatientAllergiesFor = (patientId?: number) => (state: RootState) =>
    state.census.patientAllergiesPatientId === patientId ? state.census.patientAllergies : NO_ALLERGIES;

export const selectPatientAllergiesLoading = (state: RootState) => state.census.patientAllergiesLoading;
